import { useEffect, useMemo, useState } from 'react'
import apiClient from '../../global/utils/apiClient'
import { useGolferStore } from '../store/GolferStore'
import { useMetaStore } from '../store/MetaStore'
import PrizeBoardView from '../views/PrizeBoardView'

const PrizeBoardController = () => {
  const [prizes, setPrizes] = useState([])
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)

  const orgId = useMetaStore((state) => state.orgId)
  const setPageTitle = useMetaStore((state) => state.setPageTitle)
  const golfers = useGolferStore((state) => state.golfers)
  const populateActiveGolfers = useGolferStore((state) => state.populateActiveGolfers)

  useEffect(() => {
    setPageTitle('Prizes')
    fetchPrizes()
    populateActiveGolfers()
  }, [])

  const fetchPrizes = async () => {
    try {
      if (orgId) {
        setLoading(true)
        const [prizesRes, eventsRes] = await Promise.all([
          apiClient.get(`/gskapi/prizes/org/${orgId}`),
          apiClient.get(`/gskapi/event/org/${orgId}`)
        ])
        setPrizes(prizesRes.data)
        setEvents(eventsRes.data)
      }
    } catch (error) {
      console.error('Error fetching prizes:', error)
    } finally {
      setLoading(false)
    }
  }

  const currentYear = new Date().getFullYear()

  const prizeSummary = useMemo(() => {
    if (!events.length || !golfers.length) return []

    // Get all event IDs for the current year
    const currentYearEventIds = new Set(
      events
        .filter(e => new Date(e.eventDate).getFullYear() === currentYear)
        .map(e => e.eventId)
    )

    // Filter to competitors only
    const competitors = golfers.filter(g => g.competitor)

    const summary = competitors.map(golfer => {
      // Find all prizes for this golfer from current year events
      const golferPrizes = prizes.filter(p =>
        p.golferId === golfer.golferId && currentYearEventIds.has(p.eventId)
      )

      const totalValue = golferPrizes.reduce((sum, p) => sum + parseFloat(p.prizeMoney || 0), 0)

      return {
        golferId: golfer.golferId,
        golferName: golfer.golferName,
        totalWinnings: totalValue
      }
    })

    // Sort by total winnings descending, then by name
    return summary.sort((a, b) => {
      if (b.totalWinnings !== a.totalWinnings) {
        return b.totalWinnings - a.totalWinnings
      }
      return a.golferName.localeCompare(b.golferName)
    })
  }, [events, prizes, golfers, currentYear])

  return (
    <PrizeBoardView
      prizes={prizes}
      events={events}
      loading={loading}
      prizeSummary={prizeSummary}
      currentYear={currentYear}
    />
  )
}

export default PrizeBoardController
